'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { IconNote } from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { Textarea } from '@/components/ui/textarea'

const PLACEHOLDER = 'New note...'
const AUTOSAVE_DELAY_MS = 800

interface StickyNoteWidgetProps {
  content: string
  onContentChange: (content: string) => void
  /** Hex background color (e.g. from widget settings) */
  accentColor?: string
  className?: string
}

export function StickyNoteWidget({
  content,
  onContentChange,
  accentColor,
  className
}: StickyNoteWidgetProps) {
  const [localValue, setLocalValue] = useState(content)
  const [isFocused, setIsFocused] = useState(false)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onContentChangeRef = useRef(onContentChange)
  onContentChangeRef.current = onContentChange

  useEffect(() => {
    if (!isFocused && content !== localValue) {
      setLocalValue(content)
    }
  }, [content, isFocused])

  const flushSave = useCallback(
    (value: string) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }
      const trimmed = value.trim()
      if (trimmed !== content) onContentChangeRef.current(trimmed)
    },
    [content]
  )

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    }
  }, [])

  const handleBlur = useCallback(() => {
    flushSave(localValue)
    setIsFocused(false)
  }, [localValue, flushSave])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const next = e.target.value
      setLocalValue(next)
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null
        flushSave(next)
      }, AUTOSAVE_DELAY_MS)
    },
    [flushSave]
  )

  const effectiveColor = accentColor ?? '#8B6914'
  const inkClass = effectiveColor ? 'text-white/95' : 'text-foreground'
  const mutedInkClass = effectiveColor ? 'text-white/75' : 'text-muted-foreground'

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-white/20 shadow-[0_18px_42px_-28px_rgba(15,23,42,0.65)] transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_22px_48px_-30px_rgba(15,23,42,0.75)]',
        isFocused && 'ring-2 ring-primary/30',
        className
      )}
      style={{ backgroundColor: effectiveColor }}
    >
      <div className={cn('flex shrink-0 items-center gap-1.5 px-3 pt-2.5', mutedInkClass)}>
        <IconNote size={16} />
        <span className="text-xs font-medium">Sticky note</span>
      </div>
      <Textarea
        value={localValue}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        placeholder={PLACEHOLDER}
        spellCheck
        className={cn(
          'min-h-0 flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm focus-visible:ring-0',
          effectiveColor
            ? `${inkClass} placeholder:text-white/55`
            : `${inkClass} placeholder:text-muted-foreground/60`
        )}
        rows={3}
      />
    </div>
  )
}
